import { Phone, Mail, Globe, Instagram, Facebook } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";
import { appendUtmParams } from "@/lib/url/utm";
import { useTranslate } from "@/i18n";

type Producer = Tables<"producers">;

interface ProducerContactProps {
  producer: Producer;
}

export function ProducerContact({ producer }: ProducerContactProps) {
  const t = useTranslate();
  const items = [
    producer.phone && producer.phone_public && {
      icon: Phone,
      href: `tel:${producer.phone}`,
      label: producer.phone,
    },
    producer.email && producer.email_public && {
      icon: Mail,
      href: `mailto:${producer.email}`,
      label: producer.email,
    },
    producer.website && producer.website_public && {
      icon: Globe,
      href: appendUtmParams(producer.website, {
        campaign: 'producer_page',
        content: producer.slug ?? undefined,
      }),
      label: producer.website.replace(/^https?:\/\//, ""),
      external: true,
    },
    producer.facebook && producer.facebook_public && {
      icon: Facebook,
      href: appendUtmParams(producer.facebook, {
        campaign: 'producer_page',
        content: producer.slug ?? undefined,
      }),
      label: "Facebook",
      external: true,
    },
    producer.instagram && producer.instagram_public && {
      icon: Instagram,
      href: `https://instagram.com/${producer.instagram.replace("@", "")}`,
      label: `@${producer.instagram.replace("@", "")}`,
      external: true,
    },
  ].filter(Boolean) as { icon: React.ElementType; href: string; label: string; external?: boolean }[];

  if (items.length === 0) return null;

  return (
    <section id="kontakt" className="border-t border-border/30 pt-8 scroll-mt-20">
      <div className="max-w-2xl">
        <h2 className="font-display text-lg text-muted-foreground mb-3">
          {t("producer.contact.heading")}
        </h2>

        <ul className="space-y-3">
          {items.map((item) => (
            <li key={item.href} className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted text-muted-foreground shrink-0">
                <item.icon className="h-4 w-4" />
              </div>
              <a
                href={item.href}
                {...(item.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="text-sm text-foreground hover:text-primary transition-colors break-all"
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
